import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X, User, Phone, Mail, CheckCircle } from 'lucide-react';
import { greeterAPI, assignedBookingAPI } from '../../services/api';
import { useAdminTheme } from '../../contexts/AdminThemeContext';

const BookingAssignmentModal = ({ isOpen, onClose, booking, onAssignmentSuccess }) => {
  const { isDarkMode } = useAdminTheme();
  const [availableGreeters, setAvailableGreeters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGreeterId, setSelectedGreeterId] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);

  // Fetch available greeters when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if booking is already assigned
      if (booking && booking.isAssigned) {
        toast.error('This booking is already assigned to a greeter');
        onClose();
        return;
      }
      fetchAvailableGreeters();
    }
  }, [isOpen, booking, onClose]);

  const fetchAvailableGreeters = async () => {
    try {
      setIsLoading(true);
      const response = await greeterAPI.getAvailableGreeters();
      if (response.success) {
        setAvailableGreeters(response.data.greeters);
      } else {
        toast.error('Failed to fetch available greeters');
      }
    } catch (error) {
      console.error('Error fetching available greeters:', error);
      toast.error('Failed to fetch available greeters');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignBooking = async () => {
    if (!selectedGreeterId) {
      toast.error('Please select a greeter');
      return;
    }

    try {
      setIsAssigning(true);
      const response = await assignedBookingAPI.createAssignedBooking({
        paymentRequestId: booking._id,
        greeterId: selectedGreeterId
      });

      if (response.success) {
        toast.success('Booking assigned successfully');
        onAssignmentSuccess(response.data);
        onClose();
      } else {
        toast.error(response.message || 'Failed to assign booking');
      }
    } catch (error) {
      console.error('Error assigning booking:', error);
      toast.error('Failed to assign booking');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleClose = () => {
    setSelectedGreeterId('');
    onClose();
  };

  if (!isOpen || !booking) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className={`relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md transition-colors duration-300 ${
        isDarkMode ? 'bg-skyone-card-dark border-skyone-border-dark' : 'bg-white border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className={`text-lg font-semibold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Assign Booking to Greeter
          </h3>
          <button
            onClick={handleClose}
            className={`transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Booking Details */}
        <div className={`mb-6 p-4 rounded-lg transition-colors duration-300 ${
          isDarkMode ? 'bg-skyone-inset-dark' : 'bg-gray-50'
        }`}>
          <h4 className={`text-sm font-medium mb-2 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Booking Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span className="font-medium">Customer:</span> {booking.clientName}
              </p>
              <p className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span className="font-medium">Email:</span> {booking.clientEmail}
              </p>
              {booking.phoneNumber && (
                <p className={`transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  <span className="font-medium">Phone:</span> {booking.phoneNumber}
                </p>
              )}
            </div>
            <div>
              <p className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span className="font-medium">Service:</span> {booking.serviceType}
              </p>
              <p className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span className="font-medium">Amount:</span> ${booking.amount}
              </p>
              <p className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span className="font-medium">Status:</span> 
                <span className={`ml-1 px-2 py-1 rounded-full text-xs transition-colors duration-300 ${
                  booking.status === 'paid' 
                    ? isDarkMode 
                      ? 'bg-green-900/20 text-green-400' 
                      : 'bg-green-100 text-green-800'
                    : isDarkMode
                      ? 'bg-yellow-900/20 text-yellow-400'
                      : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {booking.status}
                </span>
              </p>
            </div>
          </div>
          {booking.description && (
            <div className="mt-2">
              <p className={`transition-colors duration-300 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                <span className="font-medium">Description:</span> {booking.description}
              </p>
            </div>
          )}
        </div>

        {/* Available Greeters */}
        <div className="mb-6">
          <h4 className={`text-sm font-medium mb-3 transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Select Available Greeter</h4>
          
          {isLoading ? (
            <div className="text-center py-4">
              <div className={`animate-spin rounded-full h-6 w-6 border-b-2 mx-auto transition-colors duration-300 ${
                isDarkMode ? 'border-blue-400' : 'border-primary-600'
              }`}></div>
              <p className={`mt-2 text-sm transition-colors duration-300 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              }`}>Loading available greeters...</p>
            </div>
          ) : availableGreeters.length === 0 ? (
            <div className={`text-center py-4 transition-colors duration-300 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <User className={`w-8 h-8 mx-auto mb-2 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-300'
              }`} />
              <p>No available greeters found</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {availableGreeters.map((greeter) => (
                <div
                  key={greeter._id}
                  className={`p-3 border rounded-lg cursor-pointer transition-colors duration-300 ${
                    selectedGreeterId === greeter._id
                      ? isDarkMode 
                        ? 'border-blue-400 bg-blue-900/20' 
                        : 'border-primary-500 bg-primary-50'
                      : isDarkMode
                        ? 'border-skyone-border-dark hover:border-skyone-inset-dark bg-skyone-inset-dark'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedGreeterId(greeter._id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                          isDarkMode ? 'bg-blue-900/20' : 'bg-primary-100'
                        }`}>
                          <User className={`h-4 w-4 transition-colors duration-300 ${
                            isDarkMode ? 'text-blue-400' : 'text-primary-600'
                          }`} />
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>
                          {greeter.firstName} {greeter.lastName}
                        </p>
                        <div className={`flex items-center text-xs transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <Mail className="h-3 w-3 mr-1" />
                          {greeter.email}
                        </div>
                        <div className={`flex items-center text-xs transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          <Phone className="h-3 w-3 mr-1" />
                          {greeter.phoneNumber}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium transition-colors duration-300 ${
                        isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-800'
                      }`}>
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Available
                      </span>
                      {selectedGreeterId === greeter._id && (
                        <div className="ml-2">
                          <CheckCircle className={`h-5 w-5 transition-colors duration-300 ${
                            isDarkMode ? 'text-blue-400' : 'text-primary-600'
                          }`} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleClose}
            className={`px-4 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-300 ${
              isDarkMode 
                ? 'text-gray-300 bg-skyone-inset-dark border-skyone-border-dark hover:bg-skyone-border-dark focus:ring-blue-500' 
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50 focus:ring-primary-500'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleAssignBooking}
            disabled={!selectedGreeterId || isAssigning}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
          >
            {isAssigning ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Assigning...
              </div>
            ) : (
              'Assign Booking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingAssignmentModal;
